import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Fab,
  Paper
} from '@mui/material';
import {
  DirectionsCar,
  HealthAndSafety,
  Flight,
  Home as HomeIcon,
  AccountBalance,
  Business,
  AttachMoney,
  School,
  TwoWheeler,
  Work,
  Favorite,
  Chat
} from '@mui/icons-material';

const insuranceCategories = [
  {
    id: 'auto',
    name: 'Car Insurance',
    nameAr: 'تأمين السيارات',
    icon: DirectionsCar,
    color: '#4CAF50',
    badge: 'Claim Assurance'
  },
  {
    id: 'health',
    name: 'Health Insurance',
    nameAr: 'التأمين الصحي',
    icon: HealthAndSafety,
    color: '#2196F3',
    badge: 'PB Advantage Benefits'
  },
  {
    id: 'life',
    name: 'Term Life',
    nameAr: 'تأمين الحياة',
    icon: Favorite,
    color: '#E91E63',
    badge: 'Instant Issuance'
  },
  {
    id: 'investment',
    name: 'Investment and Life Plans',
    nameAr: 'خطط الاستثمار والحياة',
    icon: AccountBalance,
    color: '#FF9800',
    badge: 'In-Built Life Cover'
  },
  {
    id: 'travel',
    name: 'Travel Insurance',
    nameAr: 'تأمين السفر',
    icon: Flight,
    color: '#00BCD4',
    badge: 'Instant Policy'
  },
  {
    id: 'corporate_health',
    name: 'Corporate Health',
    nameAr: 'التأمين الصحي للشركات',
    icon: Business,
    color: '#673AB7',
    badge: 'Starting from AED 2*'
  },
  {
    id: 'critical_illness',
    name: 'Critical Illness',
    nameAr: 'الأمراض الخطيرة',
    icon: Favorite,
    color: '#9C27B0',
    badge: 'Cancer Cover'
  },
  {
    id: 'home',
    name: 'Home Insurance',
    nameAr: 'تأمين المنزل',
    icon: HomeIcon,
    color: '#795548',
    badge: null
  },
  {
    id: 'business',
    name: 'Business Insurance',
    nameAr: 'تأمين الأعمال',
    icon: Business,
    color: '#607D8B',
    badge: 'New'
  },
  {
    id: 'income_protection',
    name: 'Income Protection',
    nameAr: 'حماية الدخل',
    icon: AttachMoney,
    color: '#4CAF50',
    badge: 'New'
  },
  {
    id: 'child_education',
    name: 'Child Education Plan',
    nameAr: 'خطة تعليم الطفل',
    icon: School,
    color: '#FF5722',
    badge: null
  },
  {
    id: 'capital_guarantee',
    name: 'Capital Guarantee Plan',
    nameAr: 'خطة ضمان رأس المال',
    icon: AccountBalance,
    color: '#009688',
    badge: null
  },
  {
    id: 'bike',
    name: 'Bike Insurance',
    nameAr: 'تأمين الدراجات',
    icon: TwoWheeler,
    color: '#FF9800',
    badge: null
  },
  {
    id: 'professional_indemnity',
    name: 'Professional Indemnity',
    nameAr: 'التعويض المهني',
    icon: Work,
    color: '#3F51B5',
    badge: 'Best Plans'
  }
];

const Home = () => {
  const navigate = useNavigate();

  const handleCategoryClick = (categoryId) => {
    navigate(`/insurance/${categoryId}`);
  };

  const handleAIAdvisor = () => {
    navigate('/ai-advisor');
  };

  const handleBrowseAll = () => {
    navigate('/browse');
  };

  const handleCalculator = () => {
    navigate('/calculator');
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Hero Section */}
        <Paper elevation={0} sx={{ p: 4, mb: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h3" gutterBottom fontWeight="bold">
            Welcome to Tutamen
          </Typography>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Compare Insurance & Save Money
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Lebanese families save an average of $380/year with Tutamen
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleCalculator}
            >
              💰 Calculate My Savings
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ bgcolor: 'white', color: 'primary.main' }}
              onClick={handleBrowseAll}
            >
              Browse All Products
            </Button>
          </Box>
        </Paper>

        {/* Category Grid */}
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
          What type of insurance do you need?
        </Typography>

        <Grid container spacing={2}>
          {insuranceCategories.map((category) => (
            <Grid item xs={6} sm={4} md={3} key={category.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  },
                  height: '100%',
                  position: 'relative'
                }}
                onClick={() => handleCategoryClick(category.id)}
              >
                {category.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: category.badge === 'New' ? '#FF9800' : '#4CAF50',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {category.badge}
                  </Box>
                )}
                <CardContent sx={{ textAlign: 'center', pt: 3 }}>
                  <Box
                    sx={{
                      bgcolor: `${category.color}15`,
                      borderRadius: '50%',
                      width: 70,
                      height: 70,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    <category.icon sx={{ fontSize: 40, color: category.color }} />
                  </Box>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {category.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {category.nameAr}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Help Section */}
        <Paper elevation={1} sx={{ p: 3, mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Not sure which insurance you need?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Let our AI advisor ask you a few questions and recommend the best options
          </Typography>
          <Button
            variant="contained"
            startIcon={<Chat />}
            onClick={handleAIAdvisor}
            size="large"
          >
            Chat with AI Advisor
          </Button>
        </Paper>
      </Container>

      {/* Floating AI Button */}
      <Fab
        color="primary"
        aria-label="chat"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
        onClick={handleAIAdvisor}
      >
        <Chat />
      </Fab>
    </Box>
  );
};

export default Home;
